import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Clock, Loader2, Search, X, Filter, RefreshCw,
  Phone, PhoneOff, Download, Users, AlertTriangle, FileText,
  TrendingUp, PhoneCall, UserCheck
} from 'lucide-react';
import { bfrAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { saveFilters, loadFilters, clearPageFilters } from '../utils/filterPersistence';
import { BFRApprovalInsights } from '../components/DashboardApprovalInsights';

const OWNER_NATURES = ['Friendly', 'Strict', 'Good', 'Bad', 'Others'];

const NATURE_BADGE = {
  Friendly: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Strict: 'bg-red-100 text-red-700 border-red-200',
  Good: 'bg-blue-100 text-blue-700 border-blue-200',
  Bad: 'bg-orange-100 text-orange-700 border-orange-200',
  Others: 'bg-gray-100 text-gray-600 border-gray-200',
};

const PAGE_SIZES = [10, 25, 50, 100];

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

// --- Inline Call Toggle ---
const CallToggle = ({ value, onChange, testId }) => (
  <button onClick={() => onChange(value === 'yes' ? '' : 'yes')}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
      value === 'yes' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
    }`} data-testid={testId}>
    {value === 'yes' ? <Phone size={12} /> : <PhoneOff size={12} />}
    {value === 'yes' ? 'Called' : 'Not Called'}
  </button>
);

// --- Nature Select ---
const NatureSelect = ({ value, onChange, testId }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)}
    className={`px-2 py-1.5 rounded-lg text-xs font-medium border ${value ? NATURE_BADGE[value] || 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-white text-gray-500 border-gray-200'}`}
    data-testid={testId}>
    <option value="">Select...</option>
    {OWNER_NATURES.map(n => <option key={n} value={n}>{n}</option>)}
  </select>
);

// --- KPI Card ---
const KPICard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid={`bfr-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`w-9 h-9 rounded-lg bg-${color}-50 flex items-center justify-center mb-2`}>
      <Icon size={18} className={`text-${color}-500`} />
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

// --- Nature Distribution ---
const NatureDistribution = ({ data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  const total = Object.values(data).reduce((s, v) => s + v, 0);
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="bfr-nature-distribution">
      <h4 className="text-xs font-semibold text-gray-600 mb-3">Owner Nature Distribution</h4>
      <div className="space-y-2">
        {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium w-16 text-center ${NATURE_BADGE[k] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{k}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full" style={{ width: `${total > 0 ? (v / total * 100) : 0}%` }} />
            </div>
            <span className="text-xs font-medium text-gray-700 w-8 text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== CLOSED WON FEEDBACK TAB ====================
const ClosedWonFeedback = () => {
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [saving, setSaving] = useState({});

  // Filters
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [callStatusFilter, setCallStatusFilter] = useState('');
  const [ownerNatureFilter, setOwnerNatureFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getUsers();
        setUsers(Array.isArray(res.data) ? res.data : res.data?.users || []);
      } catch {}
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize, search };
      if (userFilter) params.user_filter = userFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (callStatusFilter) params.call_status = callStatusFilter;
      if (ownerNatureFilter) params.owner_nature = ownerNatureFilter;
      const res = await bfrAPI.getClosedWonFeedback(params);
      setLeads(res.data.leads || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalRecords(res.data.total || 0);
      setAnalytics(res.data.analytics || {});
    } catch (err) { console.error('BFR CW error:', err); }
    finally { setLoading(false); }
  }, [page, pageSize, search, userFilter, dateFrom, dateTo, callStatusFilter, ownerNatureFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateField = async (leadId, field, value) => {
    setSaving(p => ({ ...p, [leadId]: true }));
    try {
      await bfrAPI.updateFeedback(leadId, { feedback_type: 'closed_won', [field]: value });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, [field]: value } : l));
    } catch (err) { console.error('Update error:', err); }
    finally { setSaving(p => ({ ...p, [leadId]: false })); }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (userFilter) params.user_filter = userFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (callStatusFilter) params.call_status = callStatusFilter;
      if (ownerNatureFilter) params.owner_nature = ownerNatureFilter;
      const res = await bfrAPI.exportData('closed-won', params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `bfr_closed_won_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export error:', err); }
  };

  const a = analytics;
  const hasActiveFilters = userFilter || dateFrom || dateTo || callStatusFilter || ownerNatureFilter;

  const clearFilters = () => {
    setUserFilter(''); setDateFrom(''); setDateTo('');
    setCallStatusFilter(''); setOwnerNatureFilter(''); setPage(1);
  };

  return (
    <div className="space-y-5" data-testid="bfr-closed-won-tab">
      {/* Global Filter Bar - TOP */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${showFilters || hasActiveFilters ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="bfr-cw-toggle-filters">
              <Filter size={14} /> Filters {hasActiveFilters && <span className="w-2 h-2 bg-teal-500 rounded-full" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="bfr-cw-export">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={loadData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="bfr-cw-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50" data-testid="bfr-cw-filter-panel">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Owner/User</label>
                <select value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="bfr-cw-user-filter">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.email} value={u.name || u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Date From</label>
                <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="bfr-cw-date-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Date To</label>
                <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="bfr-cw-date-to" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Call Status</label>
                <select value={callStatusFilter} onChange={e => { setCallStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="bfr-cw-call-filter">
                  <option value="">All</option>
                  <option value="yes">Called</option>
                  <option value="no">Not Called</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Owner Nature</label>
                <select value={ownerNatureFilter} onChange={e => { setOwnerNatureFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="bfr-cw-nature-filter">
                  <option value="">All</option>
                  {OWNER_NATURES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="mt-2 text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1" data-testid="bfr-cw-clear-filters"><X size={12} /> Clear all</button>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard label="Total Leads" value={a.total_leads || 0} icon={Users} color="teal" />
        <KPICard label="New Leads" value={a.new_leads || 0} icon={TrendingUp} color="blue" />
        <KPICard label="Calls Done" value={a.calls_done || 0} icon={PhoneCall} color="emerald" />
        <KPICard label="Calls Pending" value={a.calls_pending || 0} icon={PhoneOff} color="red" />
        <NatureDistribution data={a.owner_nature_distribution} />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Table Header: Info | Search | Pagination */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 shrink-0 min-w-[180px]">
              <span>Showing {leads.length} of {totalRecords}</span>
              <span className="text-gray-300">|</span>
              <span>Page {page}/{totalPages}</span>
            </div>
            <div className="w-full sm:flex-1 sm:max-w-sm mx-auto">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search leads..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="bfr-cw-search" />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="hidden sm:inline">Per page:</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-xs" data-testid="bfr-cw-page-size">
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="bfr-cw-prev">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="bfr-cw-next">Next</button>
              </div>
            </div>
          </div>
          <div className="sm:hidden flex items-center justify-between text-xs text-gray-500 mt-2">
            <span>Showing {leads.length} of {totalRecords}</span>
            <span>Page {page}/{totalPages}</span>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 size={28} className="animate-spin text-teal-500" /></div>
        ) : leads.length > 0 ? (
          <div className="overflow-x-auto" data-testid="bfr-cw-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Hotel Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">City</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Closed Won</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">OTA</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Call</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Nature</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map(l => (
                  <tr key={l.id} className={`hover:bg-gray-50 transition-colors ${l.is_new ? 'bg-blue-50/40' : ''}`} data-testid={`bfr-cw-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {l.is_new && <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" title="New" />}
                        <span className="font-semibold text-gray-800 text-xs">{l.hotel_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{l.owner_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{l.phone || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[140px] truncate">{l.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{l.city || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(l.closed_won_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium border border-emerald-200">Completed</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CallToggle value={l.call_status} onChange={v => updateField(l.id, 'call_status', v)} testId={`bfr-call-${l.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <NatureSelect value={l.owner_nature} onChange={v => updateField(l.id, 'owner_nature', v)} testId={`bfr-nature-${l.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <input type="text" value={l.remarks || ''} placeholder="Add remarks..."
                        onChange={e => setLeads(prev => prev.map(x => x.id === l.id ? { ...x, remarks: e.target.value } : x))}
                        onBlur={e => { if (e.target.value !== (l.remarks || '')) updateField(l.id, 'remarks', e.target.value); }}
                        className="w-full min-w-[120px] px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-teal-400 outline-none"
                        data-testid={`bfr-remarks-${l.id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <CheckCircle size={40} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">No Leads Found</h3>
            <p className="text-sm text-gray-500">{hasActiveFilters ? 'Try adjusting your filters' : 'No closed won leads with all OTAs completed'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== OFFBOARDING INSIGHTS TAB ====================
const OffboardingInsights = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [saving, setSaving] = useState({});

  // Filters - restore from localStorage
  const _sf = loadFilters(user?.id, 'bfr_offboarding');
  const [userFilter, setUserFilter] = useState(_sf?.userFilter || '');
  const [dateFrom, setDateFrom] = useState(_sf?.dateFrom || '');
  const [dateTo, setDateTo] = useState(_sf?.dateTo || '');
  const [callStatusFilter, setCallStatusFilter] = useState(_sf?.callStatusFilter || '');
  const [ownerNatureFilter, setOwnerNatureFilter] = useState(_sf?.ownerNatureFilter || '');

  useEffect(() => {
    saveFilters(user?.id, 'bfr_offboarding', { userFilter, dateFrom, dateTo, callStatusFilter, ownerNatureFilter });
  }, [userFilter, dateFrom, dateTo, callStatusFilter, ownerNatureFilter, user?.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getUsers();
        setUsers(Array.isArray(res.data) ? res.data : res.data?.users || []);
      } catch {}
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize, search };
      if (userFilter) params.user_filter = userFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (callStatusFilter) params.call_status = callStatusFilter;
      if (ownerNatureFilter) params.owner_nature = ownerNatureFilter;
      const res = await bfrAPI.getOffboarding(params);
      setLeads(res.data.leads || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalRecords(res.data.total || 0);
      setAnalytics(res.data.analytics || {});
    } catch (err) { console.error('BFR Offboard error:', err); }
    finally { setLoading(false); }
  }, [page, pageSize, search, userFilter, dateFrom, dateTo, callStatusFilter, ownerNatureFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateField = async (leadId, field, value) => {
    setSaving(p => ({ ...p, [leadId]: true }));
    try {
      await bfrAPI.updateFeedback(leadId, { feedback_type: 'offboarding', [field]: value });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, [field]: value } : l));
    } catch (err) { console.error('Update error:', err); }
    finally { setSaving(p => ({ ...p, [leadId]: false })); }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (userFilter) params.user_filter = userFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (callStatusFilter) params.call_status = callStatusFilter;
      if (ownerNatureFilter) params.owner_nature = ownerNatureFilter;
      const res = await bfrAPI.exportData('offboarding', params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `bfr_offboarding_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export error:', err); }
  };

  const a = analytics;
  const hasActiveFilters = userFilter || dateFrom || dateTo || callStatusFilter || ownerNatureFilter;

  const clearFilters = () => {
    setUserFilter(''); setDateFrom(''); setDateTo('');
    setCallStatusFilter(''); setOwnerNatureFilter(''); setPage(1);
  };

  return (
    <div className="space-y-5" data-testid="bfr-offboarding-tab">
      {/* Global Filter Bar - TOP */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${showFilters || hasActiveFilters ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="bfr-ob-toggle-filters">
              <Filter size={14} /> Filters {hasActiveFilters && <span className="w-2 h-2 bg-teal-500 rounded-full" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="bfr-ob-export">
              <Download size={14} /> Export CSV
            </button>
            <button onClick={loadData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="bfr-ob-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {showFilters && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50" data-testid="bfr-ob-filter-panel">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned User</label>
                <select value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="bfr-ob-user-filter">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.email} value={u.name || u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Date From</label>
                <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="bfr-ob-date-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Date To</label>
                <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="bfr-ob-date-to" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Call Status</label>
                <select value={callStatusFilter} onChange={e => { setCallStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="bfr-ob-call-filter">
                  <option value="">All</option>
                  <option value="yes">Called</option>
                  <option value="no">Not Called</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Owner Nature</label>
                <select value={ownerNatureFilter} onChange={e => { setOwnerNatureFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="bfr-ob-nature-filter">
                  <option value="">All</option>
                  {OWNER_NATURES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="mt-2 text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1" data-testid="bfr-ob-clear-filters"><X size={12} /> Clear all</button>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard label="Total Offboarded" value={a.total_leads || 0} icon={AlertTriangle} color="orange" />
        <KPICard label="New Leads" value={a.new_leads || 0} icon={TrendingUp} color="blue" />
        <KPICard label="Calls Done" value={a.calls_done || 0} icon={PhoneCall} color="emerald" />
        <KPICard label="Calls Pending" value={a.calls_pending || 0} icon={PhoneOff} color="red" />
        <NatureDistribution data={a.owner_nature_distribution} />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Table Header: Info | Search | Pagination */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 shrink-0 min-w-[180px]">
              <span>Showing {leads.length} of {totalRecords}</span>
              <span className="text-gray-300">|</span>
              <span>Page {page}/{totalPages}</span>
            </div>
            <div className="w-full sm:flex-1 sm:max-w-sm mx-auto">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search leads..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="bfr-ob-search" />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="hidden sm:inline">Per page:</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-xs" data-testid="bfr-ob-page-size">
                  {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="bfr-ob-prev">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="bfr-ob-next">Next</button>
              </div>
            </div>
          </div>
          <div className="sm:hidden flex items-center justify-between text-xs text-gray-500 mt-2">
            <span>Showing {leads.length} of {totalRecords}</span>
            <span>Page {page}/{totalPages}</span>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 size={28} className="animate-spin text-teal-500" /></div>
        ) : leads.length > 0 ? (
          <div className="overflow-x-auto" data-testid="bfr-ob-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Hotel Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Lead ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Assigned User</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Call</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Nature</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Offboarding Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map(l => (
                    <tr key={l.id} className={`hover:bg-gray-50 transition-colors ${l.is_new ? 'bg-orange-50/40' : ''}`} data-testid={`bfr-ob-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {l.is_new && <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" title="New" />}
                          <span className="font-semibold text-gray-800 text-xs">{l.hotel_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">{l.id ? l.id.slice(0, 12) : '-'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(l.lead_date)}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{l.lead_owner || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <CallToggle value={l.call_status} onChange={v => updateField(l.id, 'call_status', v)} testId={`bfr-ob-call-${l.id}`} />
                      </td>
                      <td className="px-4 py-3">
                        <NatureSelect value={l.owner_nature} onChange={v => updateField(l.id, 'owner_nature', v)} testId={`bfr-ob-nature-${l.id}`} />
                      </td>
                      <td className="px-4 py-3">
                        <input type="text" value={l.offboarding_reason || ''} placeholder="Add reason..."
                          onChange={e => setLeads(prev => prev.map(x => x.id === l.id ? { ...x, offboarding_reason: e.target.value } : x))}
                          onBlur={e => { if (e.target.value !== (l.offboarding_reason || '')) updateField(l.id, 'offboarding_reason', e.target.value); }}
                          className="w-full min-w-[140px] px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-teal-400 outline-none"
                          data-testid={`bfr-ob-reason-${l.id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <AlertTriangle size={40} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">No Offboarded Leads</h3>
            <p className="text-sm text-gray-500">{hasActiveFilters ? 'Try adjusting your filters' : 'No offboarded leads found'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN BFR FEEDBACK DASHBOARD ====================
const BFR_TABS = [
  { key: 'closed_won', label: 'Closed Won Feedback', icon: UserCheck },
  { key: 'offboarding', label: 'Offboarding Insights', icon: AlertTriangle },
];

const BFRFeedbackDashboard = () => {
  const [activeTab, setActiveTab] = useState('closed_won');

  return (
    <div className="space-y-5" data-testid="bfr-feedback-dashboard">
      {/* BFR Approval Insights - TOP */}
      <BFRApprovalInsights />

      {/* Internal Tab Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-1 px-5 py-1" data-testid="bfr-internal-tabs">
          {BFR_TABS.map(tab => {
            const TabIcon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === tab.key ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`} data-testid={`bfr-tab-${tab.key}`}>
                <TabIcon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'closed_won' && <ClosedWonFeedback />}
      {activeTab === 'offboarding' && <OffboardingInsights />}
    </div>
  );
};

export default BFRFeedbackDashboard;
